import React from 'react';

import {Details} from '@kit';

import {cls, humanEnumeration} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import {choicesWithErrors, incompleteChoices} from '../model/choice-limits';
import {optionLabel} from '../model/choice-options';
import {FeatureInstance, flattenChoices, flattenFeatures} from '../model/feature-tree';
import {isManagedOnSheet} from '../model/spell-preparation';
import {ChoiceField} from './choice-field';
import {ClampedDescription} from './clamped-description';

import css from './master.module.css';

type Props = {
	instance: FeatureInstance;
	/** подпись под названием: уровень, подкласс */
	caption?: string;
};

export const FeatureDetails: React.FC<Props> = ({instance, caption}) => {
	const {catalog, character} = useCharacter();
	const visibleChoices = instance.choices.filter((choice) => !isManagedOnSheet(choice, instance.feature));
	const ownChoices = flattenChoices(visibleChoices);
	const hasChoices = flattenFeatures([instance]).some((feature) =>
		feature.choices.some((choice) => !isManagedOnSheet(choice, feature.feature)),
	);
	const needsChoice = incompleteChoices([instance], catalog, character).length > 0;
	const hasError = choicesWithErrors([instance], character).length > 0;
	const selectedLabels = ownChoices.flatMap((choice) =>
		choice.selected.map((key) => optionLabel(choice.group, key, catalog)),
	);

	const status = hasError ? (
		<span className={css.featureErrorText}>Повышение характеристики больше предела</span>
	) : !hasChoices ? null : needsChoice ? (
		<span className={css.required}>Требуется выбор</span>
	) : (
		<span>выбрано: {humanEnumeration(selectedLabels)}</span>
	);

	return (
		<Details
			className={cls(css.feature, needsChoice && css.featureNeedsChoice, hasError && css.featureError)}
			summary={
				<div>
					<div className={css.featureTitle}>
						{instance.feature.title}
					</div>
					{(caption || status) && (
						<div className={css.featureCaption}>
							{caption}
							{caption && status && ' • '}
							{status}
						</div>
					)}
				</div>
			}
		>
			{instance.feature.description && <ClampedDescription text={instance.feature.description} />}
			{visibleChoices.length > 0 && (
				<div className={css.choices}>
					{visibleChoices.map((choice) => (
						<ChoiceField key={choice.key} choice={choice} />
					))}
				</div>
			)}
			{instance.children.map((child) => (
				<FeatureDetails key={child.key} instance={child} />
			))}
		</Details>
	);
};
